import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers(['root', 'image', 'description']);

/** Maps Contentful image alignment to flex cross-axis alignment (image only; caption stays start-aligned). */
const ALIGNMENT_TO_ALIGN_ITEMS = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/** Shared caption / rich-text typography (blog + image description). */
const CAPTION_FONT = {
  fontSize: {base: 'sm', lg: 'md'},
  fontStyle: 'normal',
  lineHeight: '120%',
};

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "image" | "description")[]
 * }>} ImageStyles
 */
export const imageStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.Image} args.component - Image component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {ImageStyles} - Component styles
   */
  baseStyle({component}) {
    const alignmentKey = component.alignment?.toLowerCase();
    const alignItems = alignmentKey ? ALIGNMENT_TO_ALIGN_ITEMS[alignmentKey] : undefined;

    return {
      // Use flex so horizontal alignment applies only to the image/link row; `textAlign` on root
      // would inherit to captions (e.g. CF-016 / Media Carousel multi-line descriptions).
      root: alignItems
        ? {
            alignItems,
            display: 'flex',
            flexDirection: 'column',
          }
        : {},
      image: {
        display: 'inline-block',
      },
      // Image caption (plain + rich text): matches blog image-caption spec, all breakpoints
      description: {
        alignSelf: alignItems ? 'stretch' : undefined,
        // Do not inherit centered image alignment onto caption/description text
        textAlign: 'start',
        ...CAPTION_FONT,
        fontWeight: 600,
        marginTop: 3,
        paddingX: 2,
        sx: {
          '& .rich-text-content.amb-markup p:last-child, & .amb-markup p:last-child': {
            marginBottom: 0,
          },
          '& .rich-text-content.amb-markup p.chakra-text, & .rich-text-content.amb-markup .chakra-text, & .rich-text-content.amb-markup a, & .rich-text-content.amb-markup a.chakra-link, & .rich-text-content.amb-markup .chakra-link, & .amb-markup a': {
            ...CAPTION_FONT,
            fontWeight: 600,
          },
          '& .rich-text-content.amb-markup p.chakra-text:first-of-type, & .amb-markup p.chakra-text:first-of-type, & .rich-text-content.amb-markup p:first-child, & .amb-markup p:first-child': {
            fontWeight: 400,
          },
        }
      },
    };
  },
});
